import isEmpty from 'lodash/isEmpty';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { createUserWithIdp, getAuthInfo, postLogout } from '../util/api';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import config from '../config';
import { postLogin } from '../util/api';

const authenticated = authInfo => authInfo.isLogged === true && authInfo.signedInOnServer === true;  

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/Auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/Auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/Auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/Auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/Auth/LOGIN_ERROR';
export const LOGIN_PENDING_2FA = 'app/Auth/LOGIN_PENDING_2FA';

export const LOGOUT_REQUEST = 'app/Auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/Auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/Auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/Auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/Auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/Auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/Auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/Auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/Auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  isLogged: false,
  signedInOnServer: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // 2FA
  status2FA: null,
  pendingUsername: null,
  
  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true, status2FA: 'approved', pendingUsername: null, loginError: null };

    case LOGIN_PENDING_2FA:
      return { ...state, loginInProgress: false, status2FA: 'pending', pendingUsername: payload.username, loginError: null };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [], status2FA: null, pendingUsername: null};
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.Auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const loginPending2FA = (username) => ({ type: LOGIN_PENDING_2FA, payload: { username } });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch) => {
  dispatch(authInfoRequest());
  return getAuthInfo()
    .then(info => 
      dispatch(authInfoSuccess(info)))
    .catch(e => {
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password, code) => (dispatch, getState) => {
  if (authenticationInProgress( getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());
  return postLogin({username, password, code})
  .then( response => {
      
    if (response.step === 'verify_code'){
      dispatch(loginPending2FA(username));
      return { success: true, step: 'verify_code' };
    } else if (response.success) {
      dispatch(loginSuccess());
      return dispatch(fetchCurrentUser());
    } 
  })
  .catch(e => {
    dispatch(loginError(storableError(e)))
  });  
};

export const logout = () => (dispatch, getState) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());
  
  return postLogout()
  .then(() => {
    dispatch(logoutSuccess());
    dispatch(clearCurrentUser());
    log.clearUserId();
    dispatch(userLogout());
    //dispatch(authInfo());
  })
  .catch(e => dispatch(logoutError(storableError(e))));
};

export const contractSignup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const {
    email,
    password,
    firstName,
    lastName,
    pincode,
    contract_name,
    organisation_address,
    billing_code,
    billing_code_custom,
    jobtitle,
    department,
    ...rest
  } = params;

  const userTypes = config.custom.contractPincodes;

  let userType = userTypes.find(type => type.key === +pincode);

  if (!userType) {
    return Promise.reject(new Error('Invalid pincode'));
  }

  userType = userType.label;

  const createUserParams = isEmpty(rest)
    ? {
        email,
        password,
        firstName,
        lastName,
        privateData: {
          jobTitle: jobtitle,
          addressDepartment: department,
          pincode: +pincode,
          contract_name,
          address: {
            address: organisation_address.selectedPlace.address,
            building: '',
          },
          origin: {
            lat: organisation_address.selectedPlace.origin.lat,
            lng: organisation_address.selectedPlace.origin.lng,
          },
          billing_code: billing_code
            ? billing_code.key == 'custom'
              ? billing_code_custom
              : billing_code.key
            : null,
          userTypes: [userType, 'customer', 'contractCustomer'],
        },
      }
    : {
        email,
        password,
        firstName,
        lastName,
        privateData: {
          ...rest,
          jobTitle: jobtitle,
          addressDepartment: department,
          pincode: +pincode,
          contract_name,
          address: {
            address: organisation_address.selectedPlace.address,
            building: '',
          },
          origin: {
            // lat: organisation_address.
            lat: organisation_address.selectedPlace.origin.lat,
            lng: organisation_address.selectedPlace.origin.lng,
          },
          billing_code: billing_code
            ? billing_code.key == 'custom'
              ? billing_code_custom
              : billing_code.key
            : null,
          userTypes: [userType, 'customer', 'contractCustomer'],
        },
      };

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return sdk.currentUser
    .create(createUserParams)
    .then(() => dispatch(signupSuccess()))
    .then(() => dispatch(login(email, password)))
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
        userTypes: params.userTypes,
      });
    });
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const { email, password, firstName, lastName, privateData, ...rest } = params;

  let newUserTypes = [...rest?.userTypes];

  const createUserParams = isEmpty(rest)
    ? { email, password, firstName, lastName, privateData }
    : { email, password, firstName, lastName, privateData: { ...rest, userTypes: newUserTypes } };

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return sdk.currentUser
    .create(createUserParams)
    .then(() => dispatch(signupSuccess()))
    .then(() => dispatch(login(email, password)))
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
        userTypes: params.userTypes,
      });
    });
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};
